import { memo, useEffect, useState } from "react";
import { motion } from "motion/react";

import { Modal } from "../Modal";
import { ModalProps } from "../Modal/Modal";
import { ClothingItem, ImageInfo } from "@/types/product";
import styles from "./styles.module.css";
import { ProductService } from "@/services/product.service";

import EmptyProduct from "../../assets/emptyProduct.svg";
import { useImageLoading } from "@/hooks/useImageLoading";

interface ProductDetails extends Omit<ModalProps, "title" | "children"> {
  data: ClothingItem | null;
}

function ProductDetailsModal({ data, ...rest }: ProductDetails) {
  const [product, setProduct] = useState<ClothingItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { imageURI, handleImageLoad } = useImageLoading(
    (data?.product_images[0] || product?.product_images[0])?.image as ImageInfo
  );

  useEffect(() => {
    (async () => {
      try {
        if (!data) {
          return;
        }

        setProduct(null);
        setIsLoading(true);

        const response = await ProductService.getProductById(data.id);
        setProduct(response);
      } catch (error) {
        console.log("Error getting the product details.");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [data]);

  const currentProduce = product || data;
  const hasImages = currentProduce && currentProduce?.product_images?.length > 0;

  return (
    <Modal {...rest} title={data?.brand.name ?? ""}>
      {currentProduce && (
        <>
          <img
            className={styles.modalImage}
            src={hasImages ? (imageURI as string) : EmptyProduct}
            onLoad={handleImageLoad}
          />
          <div className={styles.modalGrid}>
            <DetailField label="Product" value={currentProduce?.name} loading={isLoading} />
            <DetailField label="Brand" value={currentProduce?.brand?.name} loading={isLoading} />
            {currentProduce.category_name && (
              <DetailField
                label="Category"
                value={`${product?.category_name} / ${product?.sub_category_name}`}
                loading={isLoading}
              />
            )}
            <DetailField label="Price" value={`$${currentProduce?.price_paid}`} loading={isLoading} />
            <DetailField label="Size" value={currentProduce?.size} loading={isLoading} />
            <DetailField
              label="Purchased"
              value={formatDateToYYYYMMDD(new Date(currentProduce?.purchased_at || ""))}
              loading={isLoading}
            />
            <DetailField
              label="Retailer"
              value={currentProduce?.retailer?.name || "(no retailer)"}
              loading={isLoading}
            />
          </div>
        </>
      )}
    </Modal>
  );
}

function DetailField({ label, value, loading }: { label: string; value: string; loading: boolean }) {
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: [0.4, 1, 0.4] }}
        animate={{ opacity: [1, 0.4, 1] }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: "linear"
        }}
        className={styles.detailFieldSkeleton}
      >
        <label style={{ opacity: 0 }} className={styles.detailLabel}>
          {label}
        </label>
        <span
          style={{
            opacity: 0
          }}
          className={styles.detailValue}
        >
          {value}
        </span>
      </motion.div>
    );
  }

  return (
    <div className={styles.detailField}>
      <label className={styles.detailLabel}>{label}</label>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default memo(ProductDetailsModal);
